import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { startOfWeek, format } from 'date-fns';
import { sendTaskCompletionWebhook } from '@/utils/taskCompletionWebhook';

export interface WeeklyTaskTemplate {
  id: string;
  user_name: string;
  title: string;
  description: string | null;
  hebrew_title: string | null;
  hebrew_description: string | null;
  coins: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  specific_date: string | null; // When set, this is a one-time task for this specific date
}

export interface WeeklyTaskSchedule {
  id: string;
  template_id: string;
  scheduled_day: string;
  scheduled_hour: number | null;
  scheduled_minute: number | null;
  created_at: string;
}

export interface TaskCompletion {
  id: string;
  template_id: string;
  week_start: string;
  scheduled_day: string | null;
  completed_at: string;
}

// Combined task with schedule info for display
export interface WeeklyTask extends WeeklyTaskTemplate {
  scheduled_day: string | null;
  scheduled_hour: number | null;
  scheduled_minute: number | null;
  schedule_id: string | null;
  isCompleted: boolean;
  completionId?: string;
  isOneTime?: boolean; // True if this is a one-time task (has specific_date)
}

// Template with all its schedules for management
export interface WeeklyTaskWithSchedules extends WeeklyTaskTemplate {
  schedules: WeeklyTaskSchedule[];
}

// Helper to get the week start date (Sunday) for a given offset
export const getWeekStartDate = (weekOffset: number = 0): string => {
  const today = new Date();
  const weekStart = startOfWeek(today, { weekStartsOn: 0 });
  weekStart.setDate(weekStart.getDate() + weekOffset * 7);
  return format(weekStart, 'yyyy-MM-dd');
};

export function useWeeklyTaskTemplates() {
  return useQuery({
    queryKey: ['weekly-task-templates'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('weekly_task_templates')
        .select('*')
        .eq('is_active', true)
        .order('title');

      if (error) throw error;
      return data as WeeklyTaskTemplate[];
    },
  });
}

export function useWeeklyTaskSchedules() {
  return useQuery({
    queryKey: ['weekly-task-schedules'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('weekly_task_schedule')
        .select('*')
        .order('scheduled_day')
        .order('scheduled_hour');

      if (error) throw error;
      return data as WeeklyTaskSchedule[];
    },
  });
}

export function useWeeklyTaskTemplatesWithSchedules() {
  const { data: templates = [], isLoading: templatesLoading, error: templatesError } = useWeeklyTaskTemplates();
  const { data: schedules = [], isLoading: schedulesLoading, error: schedulesError } = useWeeklyTaskSchedules();

  const templatesWithSchedules: WeeklyTaskWithSchedules[] = templates.map(template => ({
    ...template,
    schedules: schedules.filter(s => s.template_id === template.id),
  }));

  return {
    data: templatesWithSchedules,
    isLoading: templatesLoading || schedulesLoading,
    error: templatesError || schedulesError,
  };
}

export function useTaskCompletions(weekOffset: number = 0) {
  const weekStart = getWeekStartDate(weekOffset);

  return useQuery({
    queryKey: ['task-completions', weekStart],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('task_completions')
        .select('*')
        .eq('week_start', weekStart);

      if (error) throw error;
      return data as TaskCompletion[];
    },
  });
}

export function useWeeklyTasks(weekOffset: number = 0) {
  const { data: templates = [], isLoading: templatesLoading, error: templatesError } = useWeeklyTaskTemplates();
  const { data: schedules = [], isLoading: schedulesLoading, error: schedulesError } = useWeeklyTaskSchedules();
  const { data: completions = [], isLoading: completionsLoading, error: completionsError } = useTaskCompletions(weekOffset);

  // Get the week's date range for filtering one-time tasks
  const weekStart = getWeekStartDate(weekOffset);
  const weekStartDate = new Date(weekStart);
  const weekEndDate = new Date(weekStartDate);
  weekEndDate.setDate(weekEndDate.getDate() + 6);
  const weekEndStr = format(weekEndDate, 'yyyy-MM-dd');

  // Create a task entry for each schedule (same template can appear multiple times for different days)
  const weeklyTasks: WeeklyTask[] = [];

  templates.forEach(template => {
    // Check if this is a one-time task (has specific_date)
    if (template.specific_date) {
      // Only include if specific_date falls within this week
      if (template.specific_date >= weekStart && template.specific_date <= weekEndStr) {
        // Get the day of week for this specific date
        const specificDate = new Date(template.specific_date);
        const dayIndex = specificDate.getDay();
        const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
        const scheduledDay = dayNames[dayIndex];

        // Find completion for this one-time task
        const completion = completions.find(c => 
          c.template_id === template.id && c.scheduled_day === scheduledDay
        );

        // Get scheduled time from the schedule if exists
        const templateSchedules = schedules.filter(s => s.template_id === template.id);
        const schedule = templateSchedules[0];

        weeklyTasks.push({
          ...template,
          scheduled_day: scheduledDay,
          scheduled_hour: schedule?.scheduled_hour ?? null,
          scheduled_minute: schedule?.scheduled_minute ?? null,
          schedule_id: schedule?.id ?? null,
          isCompleted: !!completion,
          completionId: completion?.id,
          isOneTime: true,
        });
      }
      return; // Skip recurring logic for one-time tasks
    }

    // Recurring task logic (no specific_date)
    const templateSchedules = schedules.filter(s => s.template_id === template.id);

    if (templateSchedules.length === 0) {
      // Task without schedule - "לבצע השבוע"
      const completion = completions.find(c => 
        c.template_id === template.id && c.scheduled_day === null
      );
      weeklyTasks.push({
        ...template,
        scheduled_day: null,
        scheduled_hour: null,
        scheduled_minute: null,
        schedule_id: null,
        isCompleted: !!completion,
        completionId: completion?.id,
        isOneTime: false,
      });
    } else {
      // Create an entry for each scheduled day
      templateSchedules.forEach(schedule => {
        // Find completion for this specific template + day combination
        const completion = completions.find(c => 
          c.template_id === template.id && c.scheduled_day === schedule.scheduled_day
        );
        weeklyTasks.push({
          ...template,
          scheduled_day: schedule.scheduled_day,
          scheduled_hour: schedule.scheduled_hour,
          scheduled_minute: schedule.scheduled_minute,
          schedule_id: schedule.id,
          isCompleted: !!completion,
          completionId: completion?.id,
          isOneTime: false,
        });
      });
    }
  });

  return {
    data: weeklyTasks,
    isLoading: templatesLoading || schedulesLoading || completionsLoading,
    error: templatesError || schedulesError || completionsError,
  };
}

export function useToggleTaskCompletion() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ 
      templateId,
      scheduledDay,
      scheduledHour,
      scheduledMinute,
      weekOffset, 
      isCurrentlyCompleted,
      completionId,
      userName,
      coins,
      taskTitle,
    }: { 
      templateId: string;
      scheduledDay: string | null;
      scheduledHour?: number | null;
      scheduledMinute?: number | null;
      weekOffset: number; 
      isCurrentlyCompleted: boolean;
      completionId?: string;
      userName?: string;
      coins?: number;
      taskTitle?: string;
    }) => {
      const weekStart = getWeekStartDate(weekOffset);

      if (isCurrentlyCompleted && completionId) {
        // Remove completion
        const { error } = await supabase
          .from('task_completions')
          .delete()
          .eq('id', completionId);
        if (error) throw error;

        // Deduct coins if applicable
        if (userName && coins && coins > 0) {
          const { data: current } = await supabase
            .from('user_coin_balances')
            .select('balance')
            .eq('user_name', userName)
            .single();

          if (current) {
            const newBalance = Math.max(0, current.balance - coins);
            await supabase
              .from('user_coin_balances')
              .update({ balance: newBalance, updated_at: new Date().toISOString() })
              .eq('user_name', userName);
          }
        }
      } else {
        // Add completion with scheduled_day
        const { error } = await supabase
          .from('task_completions')
          .insert({
            template_id: templateId,
            week_start: weekStart,
            scheduled_day: scheduledDay,
          });
        if (error) throw error;

        // Add coins if applicable
        let totalUserCoins = 0;
        if (userName && coins && coins > 0) {
          const { data: current } = await supabase
            .from('user_coin_balances')
            .select('balance')
            .eq('user_name', userName)
            .single();

          if (current) {
            totalUserCoins = current.balance + coins;
            await supabase
              .from('user_coin_balances')
              .update({ balance: totalUserCoins, updated_at: new Date().toISOString() })
              .eq('user_name', userName);
          } else {
            totalUserCoins = coins;
            await supabase
              .from('user_coin_balances')
              .insert({ user_name: userName, balance: coins });
          }
        } else if (userName) {
          // Get current balance even if no coins for this task
          const { data: current } = await supabase
            .from('user_coin_balances')
            .select('balance')
            .eq('user_name', userName)
            .single();
          totalUserCoins = current?.balance || 0;
        }

        // Send webhook for task completion
        const scheduledTime = scheduledHour !== null && scheduledHour !== undefined
          ? `${scheduledHour.toString().padStart(2, '0')}:${(scheduledMinute || 0).toString().padStart(2, '0')}`
          : null;

        await sendTaskCompletionWebhook({
          taskName: taskTitle || 'Unknown Task',
          userName: userName || 'Unknown User',
          scheduledDay: scheduledDay,
          scheduledTime: scheduledTime,
          completedAt: new Date().toISOString(),
          taskCoins: coins || 0,
          totalUserCoins: totalUserCoins,
        });
      }

      return { userName };
    },
    onSuccess: (result, variables) => {
      const weekStart = getWeekStartDate(variables.weekOffset);
      queryClient.invalidateQueries({ queryKey: ['task-completions', weekStart] });
      if (result?.userName) {
        queryClient.invalidateQueries({ queryKey: ['user-coin-balance', result.userName] });
      }
    },
  });
}

export function useAddWeeklyTaskTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      title: string;
      hebrew_title: string | null;
      description: string | null;
      hebrew_description: string | null;
      coins: number;
      user_name: string;
      schedules: Array<{ scheduled_day: string; scheduled_hour: number | null; scheduled_minute: number }>;
    }) => {
      const { schedules, ...templateData } = data;

      // Insert template
      const { data: template, error: templateError } = await supabase
        .from('weekly_task_templates')
        .insert(templateData)
        .select()
        .single();

      if (templateError) throw templateError;

      // Insert schedules
      if (schedules.length > 0) {
        const schedulesWithTemplateId = schedules.map(s => ({
          ...s,
          template_id: template.id,
        }));

        const { error: schedulesError } = await supabase
          .from('weekly_task_schedule')
          .insert(schedulesWithTemplateId);

        if (schedulesError) throw schedulesError;
      }

      return template;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['weekly-task-templates'] });
      queryClient.invalidateQueries({ queryKey: ['weekly-task-schedules'] });
    },
  });
}

// Mutation for adding a one-time task (with specific_date)
export function useAddOneTimeTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      title: string;
      hebrew_title: string | null;
      description: string | null;
      hebrew_description: string | null;
      coins: number;
      user_name: string;
      specific_date: string; // yyyy-MM-dd format
      scheduled_hour: number | null;
      scheduled_minute: number;
    }) => {
      const { scheduled_hour, scheduled_minute, specific_date, ...templateData } = data;

      // Insert template with specific_date
      const { data: template, error: templateError } = await supabase
        .from('weekly_task_templates')
        .insert({
          ...templateData,
          specific_date,
        })
        .select()
        .single();

      if (templateError) throw templateError;

      // Insert schedule for time (even for one-time tasks, we store the time)
      if (scheduled_hour !== null) {
        const { error: schedulesError } = await supabase
          .from('weekly_task_schedule')
          .insert({
            template_id: template.id,
            scheduled_day: 'OneTime', // Placeholder, we use specific_date
            scheduled_hour,
            scheduled_minute,
          });

        if (schedulesError) throw schedulesError;
      }

      return template;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['weekly-task-templates'] });
      queryClient.invalidateQueries({ queryKey: ['weekly-task-schedules'] });
    },
  });
}

export function useUpdateWeeklyTaskTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ 
      id, 
      updates,
      schedules,
    }: { 
      id: string; 
      updates: Partial<Omit<WeeklyTaskTemplate, 'id' | 'created_at' | 'updated_at'>>;
      schedules: Array<{ scheduled_day: string; scheduled_hour: number | null; scheduled_minute: number }>;
    }) => {
      // Update template
      const { data, error } = await supabase
        .from('weekly_task_templates')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      // Delete old schedules
      const { error: deleteError } = await supabase
        .from('weekly_task_schedule')
        .delete()
        .eq('template_id', id);

      if (deleteError) throw deleteError;

      // Insert new schedules
      if (schedules.length > 0) {
        const schedulesWithTemplateId = schedules.map(s => ({
          ...s,
          template_id: id,
        }));

        const { error: schedulesError } = await supabase
          .from('weekly_task_schedule')
          .insert(schedulesWithTemplateId);

        if (schedulesError) throw schedulesError;
      }

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['weekly-task-templates'] });
      queryClient.invalidateQueries({ queryKey: ['weekly-task-schedules'] });
    },
  });
}

export function useDeleteWeeklyTaskTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      // Schedules are deleted automatically due to CASCADE
      const { error } = await supabase
        .from('weekly_task_templates')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['weekly-task-templates'] });
      queryClient.invalidateQueries({ queryKey: ['weekly-task-schedules'] });
    },
  });
}
